// Manual annotation creator (Unit 22, spec Step 13).
//
// Triggered by a click on blank scan area (or transcript). Renders a small
// floating form anchored to the click position; on save POSTs to
// /api/annotations/ and revalidates the SWR cache for /annotations.

import { useState } from "react";
import { mutate } from "swr";
import { useTranslation } from "react-i18next";

import { apiJson } from "../api/client";
import type { Annotation } from "../api/types";
import { annotationsKey, useAnnotationStore } from "../state/annotationStore";
import { useDeviceMode } from "../device/DeviceContext";

import "./AnnotationCreator.css";

export type ManualType = "ocr_flag" | "language" | "content" | "note";

export interface AnnotationCreatorProps {
  paperId: number;
  pageNumber: number;
  x: number;
  y: number;
  screenX: number;
  screenY: number;
  /** Optional: normalised word dimensions when triggered from a word click. */
  widthNorm?: number;
  heightNorm?: number;
  onClose: () => void;
}

export function AnnotationCreator({
  paperId,
  pageNumber,
  x,
  y,
  screenX,
  screenY,
  widthNorm = 0,
  heightNorm = 0,
  onClose,
}: AnnotationCreatorProps) {
  const { t } = useTranslation();
  const mode = useDeviceMode();
  const addAnnotation = useAnnotationStore((s) => s.addAnnotation);
  const [type, setType] = useState<ManualType>("language");
  const [text, setText] = useState("");
  const [deduction, setDeduction] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await apiJson<Annotation>("/api/annotations/", {
        method: "POST",
        body: JSON.stringify({
          paper_id: paperId,
          page_number: pageNumber,
          type,
          x_norm: x,
          y_norm: y,
          width_norm: widthNorm,
          height_norm: heightNorm,
          original_text: type === "ocr_flag" ? text : null,
          content_note: type === "note" || type === "content" ? text : null,
          accepted_text: type === "language" ? text : null,
          point_deduction: type === "note" || type === "ocr_flag" ? 0 : deduction,
        }),
      });
      addAnnotation(created);
      void mutate(annotationsKey(paperId));
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // On mobile: centre horizontally and place below the tap, clamped so the
  // panel doesn't run off the bottom of the screen.
  const positionStyle = mode === "mobile"
    ? {
        left: "50%" as const,
        transform: "translateX(-50%)",
        top: Math.min(screenY + 8, window.innerHeight - 290),
      }
    : { left: screenX, top: screenY };

  return (
    <div
      className="creator"
      style={positionStyle}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-label={t("annotation_creator.heading")}
    >
      <form onSubmit={onSave}>
        <p className="creator__head">{t("annotation_creator.heading")}</p>

        <div className="creator__types">
          {([
            ["ocr_flag", "annotation_creator.type_ocr"],
            ["language", "annotation_creator.type_language"],
            ["content", "annotation_creator.type_content"],
            ["note", "annotation_creator.type_note"],
          ] as [ManualType, string][]).map(([v, labelKey]) => (
            <label key={v} className={type === v ? "creator__type creator__type--on" : "creator__type"}>
              <input
                type="radio"
                name="annotation_type"
                value={v}
                checked={type === v}
                onChange={() => setType(v)}
              />
              {t(labelKey)}
            </label>
          ))}
        </div>

        <textarea
          className="creator__text"
          autoFocus
          value={text}
          placeholder={type === "ocr_flag" ? t("annotation_creator.placeholder_ocr") : t("annotation_creator.placeholder_text")}
          onChange={(e) => setText(e.target.value)}
          disabled={submitting}
        />

        {type !== "note" && type !== "ocr_flag" && (
          <label className="creator__deduction">
            {t("annotation_creator.points_off")}
            <input
              type="number"
              step="0.5"
              min="0"
              value={deduction}
              onChange={(e) => setDeduction(Number(e.target.value))}
              disabled={submitting}
            />
          </label>
        )}

        {error && <div className="creator__error">{error}</div>}

        <div className="creator__actions">
          <button
            type="button"
            className="creator__btn creator__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="creator__btn creator__btn--accent"
            disabled={submitting || text.trim().length === 0}
          >
            {t("common.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
